import React from "react";
import {
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
} from "@mui/material";
import {
  formatUnderscoreText,
  isTimestampField,
  isUrlField,
  isClickableUrl,
  toAbsoluteUrl,
} from "../../../utils/activityLogUtils";
import { formatDateTimeDMY } from "../../../utils/utilityFunctions";

/**
 * Fields to exclude from display in activity log details
 * Includes IDs, internal system fields, and workflow metadata
 */
const EXCLUDED_FIELDS = [
  "company_id",
  "user_id",
  "workflow_step",
  "addition_id",
  "field_type",
];

/**
 * Check if a field should be excluded from display
 * @param {string} fieldName - The field name to check
 * @returns {boolean} - True if field should be excluded
 */
const shouldExcludeField = (fieldName) => {
  const lowerFieldName = fieldName.toLowerCase();

  // Exclude specific fields
  if (EXCLUDED_FIELDS.includes(lowerFieldName)) {
    return true;
  }

  // Exclude any field ending with "_id" (case-insensitive)
  if (lowerFieldName.endsWith("_id")) {
    return true;
  }

  return false;
};

/**
 * Render JSON object as a UX-friendly table
 * Handles timestamps, URLs, and various data types
 */
export const renderJSONAsTable = (data, title, color = "default", theme) => {
  if (!data || typeof data !== "object") {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 2, bgcolor: theme.palette.grey[50], borderRadius: 1 }}
      >
        <Typography variant="body2" color="text.secondary">
          {String(data || "—")}
        </Typography>
      </Paper>
    );
  }

  // Filter out excluded fields
  const entries = Object.entries(data).filter(
    ([key]) => !shouldExcludeField(key)
  );

  // If no fields remain after filtering, return a message
  if (entries.length === 0) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 2, bgcolor: theme.palette.grey[50], borderRadius: 1 }}
      >
        <Typography variant="body2" color="text.secondary">
          No displayable details available
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                bgcolor: theme.palette.grey[100],
                fontWeight: 700,
                width: "35%",
              }}
            >
              Field
            </TableCell>
            <TableCell
              sx={{ bgcolor: theme.palette.grey[100], fontWeight: 700 }}
            >
              Value
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map(([key, value]) => {
            // Format display value based on field type
            let displayValue = value;

            // Format timestamps
            if (
              isTimestampField(key) &&
              value &&
              typeof value === "string" &&
              !value.includes("null")
            ) {
              const formatted = formatDateTimeDMY(value);
              if (formatted) {
                displayValue = formatted;
              }
            }

            // Convert relative URLs to absolute
            if (
              isUrlField(key) &&
              value &&
              typeof value === "string" &&
              !value.startsWith("http")
            ) {
              displayValue = toAbsoluteUrl(value);
            }

            const isUrl = isClickableUrl(displayValue);

            return (
              <TableRow key={key} hover>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>
                  {formatUnderscoreText(key).replace(/\b\w/g, (l) =>
                    l.toUpperCase()
                  )}
                </TableCell>
                <TableCell>
                  {typeof value === "object" ? (
                    <Typography
                      variant="caption"
                      component="pre"
                      sx={{ whiteSpace: "pre-wrap", fontSize: "0.75rem" }}
                    >
                      {JSON.stringify(value, null, 2)}
                    </Typography>
                  ) : isUrl ? (
                    <Typography
                      variant="body2"
                      component="a"
                      href={toAbsoluteUrl(displayValue)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: "primary.main",
                        textDecoration: "underline",
                        cursor: "pointer",
                        "&:hover": {
                          color: "primary.dark",
                        },
                      }}
                    >
                      {displayValue}
                    </Typography>
                  ) : (
                    <Typography variant="body2">
                      {displayValue === true
                        ? "Yes"
                        : displayValue === false
                        ? "No"
                        : typeof displayValue === "string"
                        ? formatUnderscoreText(displayValue)
                        : String(displayValue)}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
};
